// Import type definitions for data sources and block components

import type { ImageGridComponentType } from '@unifyapps/defs/blocks/ImageGrid/types';
import type { DataTableComponentType } from '@unifyapps/defs/blocks/Table/DataTable/types';
import type { BlockType } from '@unifyapps/defs/types/block';
import type { RepeatableComponentType } from '@unifyapps/defs/types/blocks';
import type { DataSource } from '@unifyapps/defs/types/dataSource';
import { getSelectedDataSourcePath } from '../../../../../../utils/getSelectedDataSourcePath';
import { cappedMemoize } from '../../../../../../utils/memoize';
import { DEFAULT_INFINITE_PAGINATION_INFO } from '../const';
import type { InfinitePaginationInfo } from '../types';
import { getPathForMappedPill } from './getPathForMappedPill';

/**
 * Information about a data source that supports infinite pagination
 */
interface InfiniteDataSourceInfo {
  limitPill: string; // Template for the limit value pill
  offsetPill: string; // Template for the offset value pill
  paginateBy?: string; // Optional pagination strategy
  dataPath: InfinitePaginationInfo['dataPath']; // Path to the data in the response
  totalCountPath: InfinitePaginationInfo['totalCountPath']; // Path to the total count in the response
}

/**
 * Configuration for a component's pagination settings
 */
interface ComponentPaginationConfig {
  dataPillPath: string; // Path to the data pill
  totalPillPath: string; // Path to the total count pill
  limitPillTemplate: string; // Template for the limit value
  offsetPillTemplate: string; // Template for the offset value
}

/**
 * Union type of block types that support pagination
 */
type BlockWithPagination =
  | BlockType<DataTableComponentType>
  | BlockType<RepeatableComponentType>
  | BlockType<ImageGridComponentType>;

/**
 * Result from checking if a block has infinite pagination
 */
interface PaginationCheckResult {
  isInfinite: boolean; // Whether the block uses infinite pagination
  infinitePaginationInfo: InfinitePaginationInfo; // Pagination configuration information
}

/**
 * Formats an array of path segments into a dot-notation path string
 * Removes the 'data' prefix if it's the first element
 */
const getPath = (pathArray?: string[]): string => {
  if (!pathArray?.length) {
    return '';
  }
  return pathArray[0] === 'data' ? pathArray.slice(1).join('.') : pathArray.join('.');
};

/**
 * Checks if a component has infinite pagination and extracts the configuration
 * @param block - The block to check
 * @param config - The pagination configuration to apply
 * @returns A record of data source IDs to their infinite pagination configuration
 */
const checkComponentPagination = (
  block: BlockWithPagination,
  config: ComponentPaginationConfig,
): Partial<Record<string, InfiniteDataSourceInfo>> => {
  const infiniteDataSourceInfo: Record<string, InfiniteDataSourceInfo> = {};
  const paginationInfo = block.component.content.addOns?.page;

  // Return empty object if pagination is not configured or is page-based
  if (!paginationInfo || paginationInfo.type === 'PAGE') {
    return {};
  }

  // Extract data source info from the component's configuration
  const dataInfo = getSelectedDataSourcePath({ data: config.dataPillPath });
  const totalInfo = getSelectedDataSourcePath({ data: (paginationInfo.total ?? '') as string });

  // Create infinite pagination configuration if both data and total count are from the same source
  if (dataInfo.dataSourceId && totalInfo.dataSourceId) {
    infiniteDataSourceInfo[dataInfo.dataSourceId] = {
      limitPill: config.limitPillTemplate,
      offsetPill: config.offsetPillTemplate,
      dataPath: getPath(dataInfo.pathArray),
      totalCountPath: getPath(totalInfo.pathArray),
    };
  }

  return infiniteDataSourceInfo;
};

/**
 * Memoized function to extract infinite pagination info from a DataTable block
 */
const getInfiniteDataSourceInfoForTableBlock = cappedMemoize(
  (block: BlockType<DataTableComponentType>): Partial<Record<string, InfiniteDataSourceInfo>> => {
    if (!block.component.content.data) {
      return {};
    }

    return checkComponentPagination(block, {
      dataPillPath: block.component.content.data,
      totalPillPath: (block.component.content.addOns?.page?.total ?? '') as string,
      limitPillTemplate: `{{ ${block.id}['content']['page']['size'] }}`,
      offsetPillTemplate: `{{ ${block.id}['content']['page']['offset'] }}`,
    });
  },
);

/**
 * Memoized function to extract infinite pagination info from a Repeatable block
 */
const getInfiniteDataSourceInfoForRepeatable = cappedMemoize(
  (block: BlockType<RepeatableComponentType>): Partial<Record<string, InfiniteDataSourceInfo>> => {
    if (!block.component.content.data) {
      return {};
    }

    return checkComponentPagination(block, {
      dataPillPath: block.component.content.data,
      totalPillPath: (block.component.content.addOns?.page?.total ?? '') as string,
      limitPillTemplate: `{{ ${block.id}['pagination']['pageSize'] }}`,
      offsetPillTemplate: `{{ ${block.id}['pagination']['offset'] }}`,
    });
  },
);

/**
 * Memoized function to extract infinite pagination info from an ImageGrid block
 */
const getInfiniteDataSourceInfoForImageGrid = cappedMemoize(
  (block: BlockType<ImageGridComponentType>): Partial<Record<string, InfiniteDataSourceInfo>> => {
    if (!block.component.content.mappedImageItems?.source) {
      return {};
    }

    return checkComponentPagination(block, {
      dataPillPath: block.component.content.mappedImageItems.source,
      totalPillPath: (block.component.content.addOns?.page?.total ?? '') as string,
      limitPillTemplate: `{{ ${block.id}['pagination']['pageSize'] }}`,
      offsetPillTemplate: `{{ ${block.id}['pagination']['offset'] }}`,
    });
  },
);

/**
 * Processes pagination settings for a block and its data source
 * @param block - The block to process
 * @param dataSource - The data source being used
 * @param dataSourceId - The ID of the data source
 * @param infinitePaginationInfo - The infinite pagination configuration
 * @returns Whether the block uses infinite pagination and its configuration
 */
const processPaginationForBlock = (
  block: BlockWithPagination,
  dataSource: Pick<DataSource, 'context' | 'inputs'>,
  dataSourceId: string,
  infinitePaginationInfo: Partial<Record<string, InfiniteDataSourceInfo>>,
): PaginationCheckResult => {
  // Return default (non-infinite) pagination if no configuration exists for this data source
  if (!infinitePaginationInfo[dataSourceId]) {
    return {
      isInfinite: false,
      infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
    };
  }

  const infinitePaginationInfoForDataSource = infinitePaginationInfo[dataSourceId];
  const { offsetPill, limitPill, dataPath, totalCountPath } = infinitePaginationInfoForDataSource;

  // Get the actual paths for the offset and limit values
  const offsetPath = getPathForMappedPill(dataSource, offsetPill);
  const limitPath = getPathForMappedPill(dataSource, limitPill);

  // Return default pagination if either path is missing
  if (!offsetPath || !limitPath) {
    return {
      isInfinite: false,
      infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
    };
  }

  // Return infinite pagination configuration
  return {
    isInfinite: true,
    infinitePaginationInfo: {
      offsetPath: offsetPath.join('.'),
      limitPath: limitPath.join('.'),
      dataPath,
      totalCountPath,
    },
  };
};

/**
 * Main function to determine if a block uses infinite pagination and extract its configuration
 * @param block - The block to check
 * @param dataSource - The data source being used
 * @param dataSourceId - The ID of the data source
 * @returns Whether the block uses infinite pagination and its configuration
 */
export const getInfinitePaginationDetailsForBlocks = ({
  block,
  dataSource,
  dataSourceId,
}: {
  block: BlockType;
  dataSource: Pick<DataSource, 'context' | 'inputs'>;
  dataSourceId: string;
}): PaginationCheckResult => {
  const componentType = block.component.componentType;

  // Handle different component types
  switch (componentType) {
    case 'Timeline':
      // Timeline components always use infinite pagination
      return {
        isInfinite: true,
        infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
      };
    case 'Table': {
      const tableBlock = block as BlockType<DataTableComponentType>;

      const paginationInfo = tableBlock.component.content.addOns?.page;
      const isInfinite = Boolean(paginationInfo && paginationInfo.type !== 'PAGE');

      // Return default pagination if not infinite
      if (!isInfinite) {
        return {
          isInfinite: false,
          infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
        };
      }

      // Process infinite pagination for Table
      const infinitePaginationInfo = getInfiniteDataSourceInfoForTableBlock(tableBlock);
      return processPaginationForBlock(
        tableBlock,
        dataSource,
        dataSourceId,
        infinitePaginationInfo,
      );
    }
    case 'Repeatable': {
      const repeatableBlock = block as BlockType<RepeatableComponentType>;
      const paginationInfo = repeatableBlock.component.content.addOns?.page;
      const isInfinite = Boolean(paginationInfo && paginationInfo.type !== 'PAGE');

      // Return default pagination if not infinite
      if (!isInfinite) {
        return {
          isInfinite: false,
          infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
        };
      }

      // Process infinite pagination for Repeatable
      const infinitePaginationInfo = getInfiniteDataSourceInfoForRepeatable(repeatableBlock);
      return processPaginationForBlock(
        repeatableBlock,
        dataSource,
        dataSourceId,
        infinitePaginationInfo,
      );
    }
    case 'ImageGrid': {
      const imageGridBlock = block as BlockType<ImageGridComponentType>;
      const paginationInfo = imageGridBlock.component.content.addOns?.page;
      const isInfinite = Boolean(paginationInfo && paginationInfo.type !== 'PAGE');

      // Return default pagination if not infinite
      if (!isInfinite) {
        return {
          isInfinite: false,
          infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
        };
      }

      // Process infinite pagination for ImageGrid
      const infinitePaginationInfo = getInfiniteDataSourceInfoForImageGrid(imageGridBlock);
      return processPaginationForBlock(
        imageGridBlock,
        dataSource,
        dataSourceId,
        infinitePaginationInfo,
      );
    }
    default: {
      // Default case for unsupported component types
      return {
        isInfinite: false,
        infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO,
      };
    }
  }
};
